import ResetSolid from '@simplywallst/ui-core/icons/ResetSolid';
import { useTheme, Alert, AlertAction } from '@simplywallst/ui-core';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import { pluralise } from '@/utilities/pluralise';

import type {
  usePortfolioLinkSettings,
  usePortfolioLinkSettingsMutation,
} from '../../hooks/usePortfolioLinkSettings';

import { LinkStatusWrapper } from './styled';

interface Props {
  portfolio: Portfolio;
  linkSettings: ReturnType<typeof usePortfolioLinkSettings>['data'];
  updateLinkSettings: ReturnType<
    typeof usePortfolioLinkSettingsMutation
  >['mutate'];
}

const SyncedState = ({
  portfolio,
  linkSettings,
  updateLinkSettings,
}: Props) => {
  const theme = useTheme();
  const numberOfHoldings = portfolio.holdings.length;
  const unmatchedHoldings = portfolio.link?.unmatchedHoldings ?? [];

  const showMatchedMessage =
    !linkSettings?.hasDismissedSuccess && numberOfHoldings > 0;
  const showUnmatchedMessage =
    !linkSettings?.hasDismissedError && unmatchedHoldings.length > 0;

  if (!showMatchedMessage && !showUnmatchedMessage) {
    return null;
  }

  return (
    <LinkStatusWrapper gap="16px">
      {showMatchedMessage ? (
        <Alert
          data-cy-id="link-successful-message"
          styleType="positive"
          actions={
            <AlertAction
              onClick={() => {
                updateLinkSettings({ hasDismissedSuccess: true });
              }}
            >
              OK
            </AlertAction>
          }
        >
          {numberOfHoldings}{' '}
          {pluralise(numberOfHoldings, 'holding', 'holdings')} imported
          successfully
        </Alert>
      ) : null}
      {showUnmatchedMessage ? (
        <Alert
          data-cy-id="link-error-message"
          icon={<ResetSolid fill={theme.color.bad} />}
          actions={
            <AlertAction
              onClick={() => {
                updateLinkSettings({ hasDismissedError: true });
              }}
            >
              OK
            </AlertAction>
          }
        >
          {unmatchedHoldings.length}{' '}
          {pluralise(unmatchedHoldings.length, 'ticker', 'tickers')} unmatched:{' '}
          {unmatchedHoldings
            .map((unmatchedHolding) => unmatchedHolding.symbol)
            .join(', ')}
          . You can manually match them.
        </Alert>
      ) : null}
    </LinkStatusWrapper>
  );
};

export default SyncedState;
